/**
 * Guides are Markdown files in this folder named `<slug>.<lang>.md`, each starting
 * with a small front matter block:
 *
 *   ---
 *   title: ...
 *   summary: ...
 *   owner: who keeps this current
 *   reviewed: 2026-10-09        (date someone last checked it against reality)
 *   status: draft | reviewed
 *   ---
 *
 * Adding a guide = adding the two files (fa and en). No backend involved.
 */
const files = import.meta.glob('./*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

export type GuideStatus = 'draft' | 'reviewed';

export interface Guide {
  slug: string;
  lang: 'fa' | 'en';
  title: string;
  summary: string;
  owner: string;
  reviewed: string;
  status: GuideStatus;
  body: string;
}

/** A guide reviewed longer ago than this is shown with an "may be out of date" notice. */
export const GUIDE_STALE_AFTER_DAYS = 180;

function parse(path: string, raw: string): Guide | null {
  const name = path.split('/').pop() ?? '';
  const match = name.match(/^(.+)\.(fa|en)\.md$/);
  const fm = raw.match(/^---\n([\s\S]*?)\n---\n/);
  if (!match || !fm) return null;
  const meta: Record<string, string> = {};
  for (const line of fm[1].split('\n')) {
    const idx = line.indexOf(':');
    if (idx > 0) meta[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  return {
    slug: match[1],
    lang: match[2] as 'fa' | 'en',
    title: meta.title ?? match[1],
    summary: meta.summary ?? '',
    owner: meta.owner ?? '',
    reviewed: meta.reviewed ?? '',
    status: meta.status === 'reviewed' ? 'reviewed' : 'draft',
    body: raw.slice(fm[0].length),
  };
}

const GUIDES = Object.entries(files)
  .map(([path, raw]) => parse(path, raw))
  .filter((g): g is Guide => g !== null);

/** Guides in the given language (falling back to the other language per slug). */
export function listGuides(language: string): Guide[] {
  const lang = language === 'fa' ? 'fa' : 'en';
  const slugs = [...new Set(GUIDES.map((g) => g.slug))].sort();
  return slugs
    .map((slug) => getGuide(slug, lang))
    .filter((g): g is Guide => g !== null);
}

export function getGuide(slug: string, language: string): Guide | null {
  const lang = language === 'fa' ? 'fa' : 'en';
  return (
    GUIDES.find((g) => g.slug === slug && g.lang === lang) ??
    GUIDES.find((g) => g.slug === slug) ??
    null
  );
}

export function isGuideStale(guide: Guide, now = new Date()): boolean {
  const reviewed = Date.parse(guide.reviewed);
  if (Number.isNaN(reviewed)) return true;
  return (now.getTime() - reviewed) / 86_400_000 > GUIDE_STALE_AFTER_DAYS;
}
